import { useState } from 'react';
import { submitTicket, submitNocobaseTicket } from '../services/api';

/**
 * A shared hook for handling the common file upload + ticket submission flow.
 *
 * Flow for staff forms:
 *  1. Upload attachments (if any)
 *  2. Submit ticket to Nocobase → receive ticketId
 *  3. Send customer acknowledgement email ONLY (via template store), using the ticketId
 *
 * The template store is NOT used for staff internal notifications — only for customer acks.
 *
 * @returns {object} { submitForm, isSubmitting, submitted, error }
 */
export function useFormSubmit() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Builds the customer-facing acknowledgement email payload.
   *
   * @param {string|number} ticketId    - The Nocobase-assigned ticket ID
   * @param {string}        customerEmail - Email address to send the acknowledgement to
   * @returns {object} Payload for submitTicket
   */
  const buildCustomerAckPayload = (ticketId, customerEmail) => ({
    name: 'customer_ticket_acknowledgement',
    variables: { ticketId: String(ticketId) },
    channel: 'EMAIL',
    recipients: [customerEmail],
    cc: ['techsupportdesk@autochek.africa'],
    subject: `Your Support Ticket Has Been Received – Ticket ID ${ticketId}`,
    metadata: {
      origin: 'staff_portal',
      correlationId: `ack-${Date.now()}`,
    },
  });

  /**
   * Resolves the customer email address from the payload.
   * Checks emailPayload variables first, then falls back to nocobasePayload.Creatoremail.
   *
   * @param {object} emailPayload    - The email variables payload
   * @param {object} nocobasePayload - The Nocobase payload (contains Creatoremail)
   * @returns {string|null}
   */
  const resolveCustomerEmail = (emailPayload, nocobasePayload) =>
    emailPayload?.variables?.email ||
    emailPayload?.variables?.userEmail ||
    nocobasePayload?.Creatoremail ||
    null;

  /**
   * Orchestrates the form submission.
   *
   * @param {object}   formData       - The current form state (must include .attachments)
   * @param {function} payloadBuilder - Receives attachmentUrls, returns { nocobasePayload, emailPayload }
   * @param {function} [onSuccess]    - Optional callback invoked after a successful submission
   */
  const submitForm = async (formData, payloadBuilder, onSuccess) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // 1. Check if any attachments are still uploading
      if (formData.attachments?.some((a) => a.isUploading)) {
        throw new Error('Please wait for all files to finish uploading before submitting.');
      }

      // 2. Build payload using the uploaded attachment objects
      const result = payloadBuilder(formData.attachments || []);

      if (result && result.nocobasePayload) {
        // 3. Submit to Nocobase and capture the ticket ID (NocoBase returns ticketid)
        const nocobaseRes = await submitNocobaseTicket(result.nocobasePayload);
        const extractedId =
          nocobaseRes?.data?.ticketid ??
          nocobaseRes?.ticketid ??
          nocobaseRes?.data?.id ??
          nocobaseRes?.id;

        const ticketId = extractedId != null ? extractedId : 'Unknown';

        // 4. Send ONLY the customer acknowledgement email via template store
        const customerEmail = resolveCustomerEmail(result.emailPayload, result.nocobasePayload);
        if (customerEmail) {
          await submitTicket(buildCustomerAckPayload(ticketId, customerEmail));
        }
      } else {
        // Legacy / simple path: payload builder returns a plain object — submit directly
        await submitTicket(result);
      }

      setSubmitted(true);

      // 5. Signal that a new ticket was created so the dashboard can refresh on remount
      try {
        localStorage.setItem('pending_ticket_refresh', 'true');
      } catch {
        // localStorage may be unavailable in some environments — non-fatal
      }

      // 6. Call the optional success callback
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Submission error:', err);
      }
      setError(err.message || 'Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    submitForm,
    isSubmitting,
    submitted,
    error,
  };
}
